import { Injectable } from "@angular/core";
import { LogoService } from "../logo.service";
import { ConfigService } from "./config.service";

// src/app/services/asset.service.ts
@Injectable({
    providedIn: 'root'
})
export class AssetService {

    private cliente = 'default';

    constructor(
        private _logoService: LogoService,
        private _configService: ConfigService
    ) {
    }

    public loadAssets(client?: string) {
        this.cliente = this._resolveClientName(client);
        const customLogo = this._resolveCustomLogo();


        if (customLogo) {
            this._logoService.Logo = customLogo;
        } else {
            this._logoService.Logo = this.getLogoPath();
        }

        this.applyFavicons();
    }

    /**
     * Resolve o clientName baseado na seguinte ordem de prioridade:
     * 1. brandingData.clientName (da API de branding)
     * 2. brandingByHost[hostname].clientName (configuração por hostname no env.json)
     * 3. Parâmetro client passado
     * 4. settings.clientName (default do env.json)
     * 5. 'default'
     */
    private _resolveClientName(client?: string): string {
        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.clientName) {
            console.log(`[AssetService] Using clientName from branding API: ${this._configService.brandingData.clientName}`);
            return this._configService.brandingData.clientName;
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.clientName) {
            console.log(`[AssetService] Using clientName from brandingByHost: ${possibleHosts[host].clientName}`);
            return possibleHosts[host].clientName;
        }

        // 3. Terceiro: parâmetro passado
        if (client && client.trim() !== '') {
            console.log(`[AssetService] Using clientName from parameter: ${client}`);
            return client;
        }

        // 4. Quarto: settings.clientName (default do env.json)
        if (this._configService.settings?.clientName) {
            console.log(`[AssetService] Using clientName from settings: ${this._configService.settings.clientName}`);
            return this._configService.settings.clientName;
        }

        // 5. Fallback final
        console.warn(`[AssetService] No clientName found, using default`);
        return 'default';
    }

    /**
     * Resolve logo customizado baseado na seguinte ordem de prioridade:
     * 1. brandingData.logo (da API de branding)
     * 2. brandingByHost[hostname].logo (configuração por hostname no env.json)
     * 3. null (usa logo padrão do cliente via getLogoPath)
     */
    private _resolveCustomLogo(): string | null {
        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.logo) {
            return this._configService.brandingData.logo;
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.logo) {
            return possibleHosts[host].logo;
        }

        // Sem logo customizado
        return null;
    }

    /**
     * Resolve imagem da tela de login customizada baseado na seguinte ordem de prioridade:
     * 1. brandingData.loginScreenImage (da API de branding)
     * 2. brandingByHost[hostname].loginScreenImage (configuração por hostname no env.json)
     * 3. null (usa imagem padrão do cliente via path local)
     */
    private _resolveLoginScreenImage(): string | null {
        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.loginScreenImage) {
            return this._configService.brandingData.loginScreenImage;
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.loginScreenImage) {
            return possibleHosts[host].loginScreenImage;
        }

        // Sem imagem customizada
        return null;
    }

    /**
     * Resolve favicon customizado baseado na seguinte ordem de prioridade:
     * 1. brandingData.favicon{size} (da API de branding)
     * 2. brandingByHost[hostname].favicon{size} (configuração por hostname no env.json)
     * 3. null (usa favicon padrão do cliente via path local)
     */
    private _resolveFaviconUrl(size: '16x16' | '32x32'): string | null {
        const faviconKey = size === '16x16' ? 'favicon16x16' : 'favicon32x32';

        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.[faviconKey]) {
            return this._configService.brandingData[faviconKey];
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.[faviconKey]) {
            return possibleHosts[host][faviconKey];
        }

        // Sem favicon customizado
        return null;
    }

    public getLogoPath(): string {
        if (this.cliente == 'btg') {
            //btg usa svg
            return `assets/${this.cliente}/imgs/logo2.svg`;
        }
        return `assets/${this.cliente}/imgs/logo.png`;
    }

    /**
     * Retorna o path da imagem de login seguindo prioridade:
     * 1. Imagem customizada (brandingData ou brandingByHost)
     * 2. Fallback: assets/{clientName}/imgs/login-image.png
     */
    public getLoginImgPath(): string {
        const customImage = this._resolveLoginScreenImage();
        if (customImage) {
            return customImage;
        }
        // Fallback para imagem local
        return `assets/${this.cliente}/imgs/login-image.png`;
    }

    /**
     * Retorna o path do favicon seguindo prioridade:
     * 1. Favicon customizado (brandingData ou brandingByHost) - apenas para 16x16 e 32x32
     * 2. Fallback: assets/{clientName}/icon/favicon-{size}.png
     */
    public getFavicon(size: '16x16' | '32x32' | 'apple'): string {
        // Para 16x16 e 32x32, tenta resolver favicon customizado
        if (size === '16x16' || size === '32x32') {
            const customFavicon = this._resolveFaviconUrl(size);
            if (customFavicon) {
                return customFavicon;
            }
        }

        // Fallback para favicon local
        switch (size) {
            case '16x16':
                return `assets/${this.cliente}/icon/favicon-16x16.png`;
            case '32x32':
                return `assets/${this.cliente}/icon/favicon-32x32.png`;
            case 'apple':
                return `assets/${this.cliente}/icon/apple-touch-icon.png`;
            default:
                return `assets/${this.cliente}/icon/favicon-16x16.png`;
        }
    }

    async getTutorialPdfPath(): Promise<string> {
        const path = `assets/${this.cliente}/documents/tutorial.pdf`;

        try {
            const res = await fetch(path, { method: 'HEAD' });
            if (res.ok) {
                return path;
            } else {
                return 'assets/documents/tutorial.pdf';
            }
        } catch {
            return 'assets/documents/tutorial.pdf';
        }
    }


    private applyFavicons(): void {
        this.updateOrCreateLink('favicon-16x16', this.getFavicon('16x16'), 'image/png', '16x16');
        this.updateOrCreateLink('favicon-32x32', this.getFavicon('32x32'), 'image/png', '32x32');
        this.updateOrCreateLink('apple-touch-icon', this.getFavicon('apple'), 'image/png');
    }

    private updateOrCreateLink(id: string, href: string, type: string, sizes?: string) {
        const existingLink = document.getElementById(id);
        if (existingLink) {
            document.head.removeChild(existingLink);
        }

        const link = document.createElement('link');
        link.id = id;
        link.rel = id === 'apple-touch-icon' ? 'apple-touch-icon' : 'icon';
        link.setAttribute('type', type);
        link.setAttribute('href', `${href}?v=${new Date().getTime()}`);
        if (sizes) {
            link.setAttribute('sizes', sizes);
        }

        document.head.appendChild(link);
    }
}
